import { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * Cinematic reveal of the winner screen.
 *
 *   [badge]  →  title  →  image  →  fmv  →  cta
 *
 * `badge` opens the show: glow bloom rises and the outline letters
 * (GRAIL / CHASE / …) widen out from a single vertical sliver in
 * the centre. The title typewriter only starts once the backdrop
 * has settled, so the headline reads against an atmospheric, not
 * empty, frame.
 *
 * `title` waits for the typewriter's own completion callback —
 * long card names ("2024 PANINI PRIZM GOLD CHOICE JAMES COOK 29
 * PSA 9") get to finish typing before the next slot lifts in.
 * Every later step auto-advances on a fixed delay so the
 * sequence reads like one choreographed beat instead of a
 * blur of pop-ins.
 *
 * `badge` is dropped from the order when the item has no badge —
 * no point sitting on an empty pause waiting for a backdrop
 * that won't render.
 *
 * The profit figure no longer has its own bottom-panel beat — the
 * bottom panel only ever shows Fair Market Value (`fmv`).
 */
export type RevealStage =
  | 'title'
  | 'image'
  | 'badge'
  | 'fmv'
  | 'cta'
  | 'shipping';

/** How long the named stage stays on stage before the next slot
 *  is unveiled. Beats with an inner odometer (profit / fmv) play
 *  a three-part choreography:
 *
 *    600 ms  — fade + lift entrance lands
 *    ROLL_START_DELAY_MS — paused-on-zero so the eye reads "0.00"
 *    ~900–1100 ms — odometer rolls (durationMs + per-digit stagger)
 *    ~250 ms — settled breath before handing off to the next beat
 *
 *  Numbers below bake those in. `shipping` is the last stage, so its
 *  hold isn't used. */
const STAGE_HOLD_MS: Record<RevealStage, number> = {
  // Bloom (1.4 s) + outline letters widen from centre (≈1.0 s) +
  // a short settle before the title drops in.
  badge: 1500,
  // The title now slides DOWN into place (no typewriter), so the beat
  // just leads the card by the slide's length before the image lifts.
  // Tightened (title/image/fmv) so the headline + card + numbers land a
  // touch quicker and the buyback CTA goes live sooner.
  title: 330,
  image: 500,
  // The CTA lifts in ~0.4s after FMV — NOT waiting for the odometer to
  // finish. FMV and the Accept Buyback button appear almost together and
  // both roll their numbers in parallel, instead of the button stalling
  // for the whole FMV count-up.
  fmv: 380,
  // Accept Buyback holds a beat, THEN the secondary Create Shipping link
  // drops in — so the bottom reads FMV → Accept Buyback → Create Shipping
  // instead of the button + link arriving together.
  cta: 350,
  shipping: 0,
};

/** How long after a row becomes visible we hold its odometer
 *  parked on zeros before rolling. Slightly longer than the row's
 *  600 ms entrance so the eye registers the "$0.00" frame before
 *  the slot reels start spinning. */
export const ROLL_START_DELAY_MS = 300;

interface UseRevealSequenceOptions {
  /** Whether the item carries a badge (Grail / Chase / Rare / …).
   *  When false the `badge` beat is dropped from the order — no
   *  point holding a 1.7 s pause for a backdrop that won't render. */
  hasBadge: boolean;
  /** Returning view — skip the cascade entirely and start on the
   *  LAST stage so `visible()` reports true for every beat from the
   *  first render. No cascade timers ever fire. Default false. */
  instant?: boolean;
  /** Post-shuffle episode — the title + card are already on screen
   *  (the reel just delivered the card), so skip their beats and start
   *  the cascade at `image`: title + image read as visible immediately
   *  and only the bottom (FMV → Buyback CTA) animates in. Default false. */
  quickHeader?: boolean;
}

export function useRevealSequence({
  hasBadge,
  instant = false,
  quickHeader = false,
}: UseRevealSequenceOptions) {
  const order = useMemo<RevealStage[]>(() => {
    const stages: RevealStage[] = [];
    if (hasBadge) stages.push('badge');
    stages.push('title', 'image', 'fmv', 'cta', 'shipping');
    return stages;
  }, [hasBadge]);

  // Start on whichever stage `order` opens with (badge if present,
  // else title). Lazy init: `hasBadge` is stable for the lifetime of
  // WinnerStep, so we don't need to react to later changes. When
  // `instant`, jump straight to the LAST stage so every beat is
  // `visible()` from the first render and no timers run. When
  // `quickHeader`, start on `image` so title + image are immediately
  // visible and only the FMV → CTA cascade plays.
  const [stage, setStage] = useState<RevealStage>(() => {
    if (instant) return order[order.length - 1]!;
    if (quickHeader) {
      const i = order.indexOf('image');
      return i >= 0 ? order[i]! : order[0]!;
    }
    return order[0]!;
  });

  const advance = useCallback(() => {
    setStage((current) => {
      const i = order.indexOf(current);
      return i >= 0 && i < order.length - 1 ? order[i + 1]! : current;
    });
  }, [order]);

  // Every beat auto-advances after its hold — including `title`, which
  // used to wait on the typewriter's completion. The title now drops in
  // independently (a slide, not a type), so the cascade flows on timers
  // alone (this also avoids a badged item stalling at `title` when the
  // instant title fired its completion during the earlier `badge` beat).
  useEffect(() => {
    if (stage === order[order.length - 1]) return;
    const t = window.setTimeout(advance, STAGE_HOLD_MS[stage]);
    return () => window.clearTimeout(t);
  }, [stage, order, advance]);

  const visible = useCallback(
    (target: RevealStage) => {
      const targetIdx = order.indexOf(target);
      if (targetIdx === -1) return false;
      return order.indexOf(stage) >= targetIdx;
    },
    [stage, order],
  );

  return { visible };
}
